import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatDate, formatDateLong, formatCurrency, getStockStatus, getImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Package, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Upload, Download, X, TrendingUp, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { validateSku } from "@/lib/validation";
import CSVImportDialog from "@/components/CSVImportDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  product_name: "", description: "", category: "", subcategory: "",
  unit_of_measure: "Each", length: "", width: "", height: "",
  dimension_unit: "cm", weight: "", weight_unit: "kg",
  cost_price: "", selling_price: "", quantity_in_stock: 0,
  reorder_level: 0, primary_supplier: "", image: "", status: "Active",
};

export default function InventoryPage() {
  const { id } = useParams();
  if (id) return <ProductDetail productId={id} />;
  return <ProductList />;
}

function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, lowStock: 0, value: 0 });
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canEdit = ["admin", "sales_manager"].includes(user?.role);
  const canDelete = ["admin", "sales_manager"].includes(user?.role);

  const handleExport = async () => {
    try {
      const res = await api.get("/export/products", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Products exported successfully");
    } catch { toast.error("Failed to export products"); }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, category: categoryFilter, stock_level: stockFilter, page, limit: 25 };
      const res = await api.get("/products", { params });
      setProducts(res.data.products);
      setTotalPages(res.data.pages);
      setStats({ total: res.data.total_products, lowStock: res.data.low_stock_count, value: res.data.total_value });
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [search, categoryFilter, stockFilter, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    api.get("/suppliers", { params: { limit: 100 } }).then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("add") === "true" && canEdit) {
      setForm({ ...emptyForm }); setEditProduct(null); setDialogOpen(true);
    }
  }, [searchParams, canEdit]);

  const handleSave = async () => {
    if (!(form.product_name || "").trim()) { toast.error("Product Name is required"); return; }
    if (form.cost_price && parseFloat(form.cost_price) <= 0) { toast.error("Cost price must be greater than zero"); return; }
    if (form.selling_price && form.cost_price && parseFloat(form.selling_price) < parseFloat(form.cost_price)) { toast.error("Selling price must be greater than cost price"); return; }
    if (!editProduct && !form.sku) { toast.error("SKU is required"); return; }
    if (form.sku) {
      const v = validateSku(form.sku);
      if (!v.valid) { toast.error(v.error); return; }
    }
    setSaving(true);
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct.product_id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product created");
      }
      setDialogOpen(false); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Product deleted"); setDeleteId(null); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const openEdit = (p) => { setEditProduct(p); setForm({ ...emptyForm, ...p }); setDialogOpen(true); };
  const openAdd = () => { setEditProduct(null); setForm({ ...emptyForm }); setDialogOpen(true); };

  const stockBadge = (p) => {
    const status = getStockStatus(p.quantity_in_stock, p.reorder_level);
    if (status === "Out of Stock") return "bg-red-50 text-red-700 border-red-200";
    if (status === "Low Stock") return "bg-yellow-50 text-yellow-700 border-yellow-200";
    return "bg-green-50 text-green-700 border-green-200";
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    if (selected.length === products.length) setSelected([]);
    else setSelected(products.map((p) => p.product_id));
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await api.post("/products/bulk-delete", { product_ids: selected });
      toast.success(`${res.data.deleted_count} products deleted successfully`);
      setSelected([]);
      setBulkDeleteOpen(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk delete failed"); }
    finally { setBulkDeleting(false); }
  };

  const selectedProducts = products.filter((p) => selected.includes(p.product_id));

  return (
    <div className="animate-fade-in" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Inventory</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <span className="text-xs text-gray-500">{stats.total} products</span>
            <span className="text-xs text-gray-300">|</span>
            <span className="text-xs text-[#F59E0B] font-medium">{stats.lowStock} low stock</span>
            <span className="text-xs text-gray-300">|</span>
            <span className="text-xs text-[#10B981] font-medium">Value: {formatCurrency(stats.value)}</span>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button data-testid="export-products-button" variant="outline" onClick={handleExport} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="import-products-button" variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="add-product-button" onClick={openAdd} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add New Product
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="product-search-input" placeholder="Search by SKU or name..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9 rounded-sm border-gray-300" />
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="product-category-filter" className="w-[180px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.filter((c) => c.status === "Active").map((c) => (
              <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={(v) => { setStockFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="product-stock-filter" className="w-[150px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Stock" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stock</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="low_stock">Low Stock</SelectItem>
            <SelectItem value="out_of_stock">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        {/* Bulk actions bar */}
        {selected.length > 0 && (
          <div data-testid="bulk-actions-bar" className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b-2 border-[#E63946]">
            <span className="text-sm font-medium text-[#1F2937]">{selected.length} item{selected.length > 1 ? "s" : ""} selected</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelected([])} className="rounded-sm text-xs">Deselect All</Button>
              {canDelete && (
                <Button data-testid="bulk-delete-button" size="sm" onClick={() => setBulkDeleteOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm text-xs">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
                </Button>
              )}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 w-10"><Checkbox checked={products.length > 0 && selected.length === products.length} onCheckedChange={toggleAll} /></th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">SKU</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Category</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cost</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sell</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Margin</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</th>
                <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-b border-gray-100"><td colSpan={10} className="px-3 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>)
              ) : products.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">
                  <Package className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No products found</p>
                </td></tr>
              ) : (
                products.map((p) => {
                  const catName = categories.find((c) => c.category_id === p.category)?.name || "-";
                  const status = getStockStatus(p.quantity_in_stock, p.reorder_level);
                  return (
                    <tr key={p.product_id} data-testid={`product-row-${p.product_id}`}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/inventory/${p.product_id}`)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.includes(p.product_id)} onCheckedChange={() => toggleSelect(p.product_id)} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-sm bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                            {p.image ? <img src={getImageUrl(p.image)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Package className="w-4 h-4 text-gray-400" />}
                          </div>
                          <span className="font-medium text-[#1F2937] truncate max-w-[200px]">{p.product_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-500 font-mono text-xs hidden md:table-cell">{p.sku}</td>
                      <td className="px-3 py-3 text-gray-600 hidden lg:table-cell">{catName}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{formatCurrency(p.cost_price)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{formatCurrency(p.selling_price)}</td>
                      <td className="px-3 py-3 text-right hidden md:table-cell">
                        <span className={p.profit_margin >= 0 ? "text-[#10B981]" : "text-[#E63946]"}>{p.profit_margin?.toFixed(2)}%</span>
                      </td>
                      <td className="px-3 py-3 text-right font-medium">{p.quantity_in_stock}</td>
                      <td className="px-3 py-3"><Badge className={`rounded-full text-xs ${stockBadge(p)}`}>{status}</Badge></td>
                      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && <Button data-testid={`edit-product-${p.product_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>}
                          {canDelete && <Button data-testid={`delete-product-${p.product_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(p.product_id)}><Trash2 className="w-4 h-4" /></Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editProduct ? "Edit Product" : "Add New Product"}</DialogTitle></DialogHeader>
          <ProductForm form={form} setForm={setForm} categories={categories} suppliers={suppliers} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-product-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? "Saving..." : "Save Product"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Are you sure?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this product.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product" onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Products?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span>Are you sure you want to delete {selected.length} selected products? This action cannot be undone.</span>
              <ul className="mt-2 space-y-1">
                {selectedProducts.slice(0, 5).map((p) => (
                  <li key={p.product_id} className="text-sm text-gray-600 flex items-center gap-1.5">
                    <span className="font-mono text-xs text-gray-400">{p.sku}</span> {p.product_name}
                  </li>
                ))}
                {selectedProducts.length > 5 && <li className="text-sm text-gray-500">...and {selectedProducts.length - 5} more products</li>}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete" onClick={handleBulkDelete} disabled={bulkDeleting} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {bulkDeleting ? "Deleting..." : `Delete ${selected.length} Products`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CSVImportDialog open={importOpen} onOpenChange={setImportOpen} entityType="products" onComplete={fetchProducts} />
    </div>
  );
}

function ProductForm({ form, setForm, categories, suppliers }) {
  const [uploading, setUploading] = useState(false);
  const [skuError, setSkuError] = useState(null);
  const [skuChecking, setSkuChecking] = useState(false);
  const [suggestedSku, setSuggestedSku] = useState("");
  const [nameError, setNameError] = useState(null);
  const costPrice = parseFloat(form.cost_price) || 0;
  const sellPrice = parseFloat(form.selling_price) || 0;
  const profitAmount = sellPrice - costPrice;
  const profitMargin = costPrice > 0 ? ((sellPrice - costPrice) / costPrice * 100) : 0;

  // Fetch suggested SKU on mount (only for new products)
  useState(() => {
    if (!form.sku && !form.product_id) {
      api.get("/products/next-sku").then((r) => setSuggestedSku(r.data.suggested_sku)).catch(() => {});
    }
  });

  const handleSkuBlur = async () => {
    const sku = (form.sku || "").trim();
    if (!sku) { setSkuError(null); return; }
    const v = validateSku(sku);
    if (!v.valid) { setSkuError(v.error); return; }
    setSkuChecking(true);
    try {
      const res = await api.post("/products/check-sku", { sku, exclude_product_id: form.product_id || null });
      if (res.data.exists) {
        setSkuError(`SKU '${sku}' is already used by "${res.data.product.product_name}". Try: ${sku}-A, ${sku}-2`);
      } else {
        setSkuError(null);
      }
    } catch { setSkuError(null); }
    finally { setSkuChecking(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image: res.data.url });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const f = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="space-y-6">
      {/* Image Upload */}
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-2">Product Image</label>
        {form.image ? (
          <div className="relative w-32 h-32 rounded-sm overflow-hidden border border-gray-200">
            <img src={getImageUrl(form.image)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setForm({ ...form, image: "" })} className="absolute top-1 right-1 w-6 h-6 bg-[#E63946] text-white rounded-full flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label data-testid="product-image-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-sm bg-gray-50 cursor-pointer hover:border-[#E63946] transition-colors">
            <Upload className="w-6 h-6 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">{uploading ? "Uploading..." : "Click to upload"}</span>
            <span className="text-xs text-gray-400 mt-1">PNG, JPG, JPEG up to 5MB</span>
            <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp" onChange={handleImageUpload} className="hidden" disabled={uploading} />
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {/* SKU Field */}
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">SKU Code <span className="text-[#E63946]">*</span></label>
          <div className="relative">
            <Input data-testid="product-sku-input" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value.replace(/\s/g, "") })}
              onBlur={handleSkuBlur} placeholder={suggestedSku ? `Enter custom or use: ${suggestedSku}` : "Enter SKU code"}
              className={`rounded-sm h-10 font-mono text-sm ${skuError ? "border-[#E63946]" : "border-gray-300"}`} />
            {skuChecking && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
            {!skuError && form.sku && !skuChecking && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#10B981]" />}
          </div>
          {skuError && <p data-testid="sku-error" className="text-xs text-[#E63946] mt-1">{skuError}</p>}
          {suggestedSku && !form.sku && (
            <button type="button" onClick={() => setForm({ ...form, sku: suggestedSku })} className="text-xs text-[#E63946] mt-1 hover:underline">
              Use suggested: {suggestedSku}
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Product Name <span className="text-[#E63946]">*</span></label>
          <Input data-testid="product-name-input" value={form.product_name} onChange={(e) => { setForm({ ...form, product_name: e.target.value }); if (e.target.value.trim()) setNameError(null); }}
            onBlur={() => { if (!(form.product_name || "").trim()) setNameError("Product Name is required"); }}
            placeholder="Enter product name" className={`rounded-sm h-10 ${nameError ? "border-[#E63946]" : "border-gray-300"}`} />
          {nameError && <p data-testid="product-name-error" className="text-xs text-[#E63946] mt-1">{nameError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Category</label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="product-category-select" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {categories.filter((c) => c.status === "Active").map((c) => (
                <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#374151] mb-1">Description</label>
          <textarea data-testid="product-description" value={form.description} onChange={f("description")} placeholder="Product description..."
            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Unit of Measure</label>
          <Select value={form.unit_of_measure} onValueChange={(v) => setForm({ ...form, unit_of_measure: v })}>
            <SelectTrigger className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Each", "Box", "Pallet", "KG", "Liter", "Meter", "Roll", "Pack"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Primary Supplier</label>
          <Select value={form.primary_supplier} onValueChange={(v) => setForm({ ...form, primary_supplier: v })}>
            <SelectTrigger data-testid="product-supplier-select" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select supplier" /></SelectTrigger>
            <SelectContent>
              {suppliers.filter((s) => s.status === "Active").map((s) => (
                <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Cost Price (ZAR) <span className="text-[#E63946]">*</span></label>
          <Input data-testid="product-cost-price" type="number" step="0.01" value={form.cost_price} onChange={f("cost_price")} placeholder="0.00" className="rounded-sm border-gray-300 h-10" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Selling Price (ZAR) <span className="text-[#E63946]">*</span></label>
          <Input data-testid="product-selling-price" type="number" step="0.01" value={form.selling_price} onChange={f("selling_price")} placeholder="0.00" className="rounded-sm border-gray-300 h-10" />
        </div>

        {costPrice > 0 && sellPrice > 0 && (
          <div className="md:col-span-2 bg-gray-50 rounded-sm p-4 border border-gray-200">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Profit Amount</p>
                <p className={`font-heading font-bold text-lg ${profitAmount >= 0 ? "text-[#10B981]" : "text-[#E63946]"}`}>{formatCurrency(profitAmount)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Profit Margin</p>
                <p className={`font-heading font-bold text-lg ${profitMargin >= 0 ? "text-[#10B981]" : "text-[#E63946]"}`}>{profitMargin.toFixed(2)}%</p>
              </div>
              {profitMargin < 10 && profitMargin >= 0 && (
                <div className="flex items-center gap-1 text-[#F59E0B]">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-medium">Low margin warning</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Quantity in Stock</label>
          <Input data-testid="product-quantity" type="number" value={form.quantity_in_stock} onChange={(e) => setForm({ ...form, quantity_in_stock: parseInt(e.target.value) || 0 })} className="rounded-sm border-gray-300 h-10" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Reorder Level</label>
          <Input data-testid="product-reorder-level" type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: parseInt(e.target.value) || 0 })} className="rounded-sm border-gray-300 h-10" />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#374151] mb-2">Dimensions</label>
          <div className="flex flex-wrap gap-3">
            <Input value={form.length} onChange={f("length")} placeholder="Length" className="w-24 rounded-sm border-gray-300 h-9 text-sm" />
            <Input value={form.width} onChange={f("width")} placeholder="Width" className="w-24 rounded-sm border-gray-300 h-9 text-sm" />
            <Input value={form.height} onChange={f("height")} placeholder="Height" className="w-24 rounded-sm border-gray-300 h-9 text-sm" />
            <Select value={form.dimension_unit} onValueChange={(v) => setForm({ ...form, dimension_unit: v })}>
              <SelectTrigger className="w-20 rounded-sm border-gray-300 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["mm", "cm", "m", "inches"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={form.weight} onChange={f("weight")} placeholder="Weight" className="w-24 rounded-sm border-gray-300 h-9 text-sm" />
            <Select value={form.weight_unit} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
              <SelectTrigger className="w-20 rounded-sm border-gray-300 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["g", "kg", "lbs"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Status</label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="product-status-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Discontinued">Discontinued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">VAT Classification</label>
          <Select value={form.vat_code || "standard"} onValueChange={(v) => setForm({ ...form, vat_code: v, zero_rated_reason: v !== "zero-rated" ? "" : form.zero_rated_reason })}>
            <SelectTrigger data-testid="product-vat-code" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard Rate (15%)</SelectItem>
              <SelectItem value="zero-rated">Zero-Rated (0%)</SelectItem>
              <SelectItem value="exempt">Exempt (No VAT)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.vat_code === "zero-rated" && (
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1">Zero-Rated Reason *</label>
            <select data-testid="product-zero-rated-reason" value={form.zero_rated_reason || ""} onChange={(e) => setForm({ ...form, zero_rated_reason: e.target.value })}
              className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-[#E63946]">
              <option value="">Select reason...</option>
              <option value="export">Export (Goods leaving SA)</option>
              <option value="basic-food">Basic Foodstuff (19 items list)</option>
              <option value="fuel">Fuel (Petrol/Diesel)</option>
              <option value="international-transport">International Transport</option>
              <option value="other">Other (specify in notes)</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductDetail({ productId }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "sales_manager", "sales_rep"].includes(user?.role);

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => {
      setProduct(r.data); setForm({ ...emptyForm, ...r.data });
    }).catch(() => toast.error("Product not found")).finally(() => setLoading(false));
    api.get("/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    api.get("/suppliers", { params: { limit: 100 } }).then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {});
  }, [productId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/products/${productId}`, form);
      setProduct(res.data); setEditOpen(false); toast.success("Product updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!product) return <p className="text-gray-400">Product not found</p>;

  const stockStatus = getStockStatus(product.quantity_in_stock, product.reorder_level);
  const stockClass = stockStatus === "Out of Stock" ? "bg-red-50 text-red-700 border-red-200" : stockStatus === "Low Stock" ? "bg-yellow-50 text-yellow-700 border-yellow-200" : "bg-green-50 text-green-700 border-green-200";

  return (
    <div className="animate-fade-in" data-testid="product-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/inventory")} className="h-8 w-8"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{product.product_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-xs text-gray-500">{product.sku}</span>
            <Badge className={`rounded-full text-xs ${stockClass}`}>{stockStatus}</Badge>
          </div>
        </div>
        {canEdit && (
          <Button data-testid="edit-product-detail" onClick={() => setEditOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Image */}
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            <div className="aspect-square bg-gray-100 rounded-sm overflow-hidden flex items-center justify-center">
              {product.image ? (
                <img src={getImageUrl(product.image)} alt={product.product_name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-16 h-16 text-gray-300" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <Card className="lg:col-span-2 border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            {/* Profit highlight */}
            <div className="bg-gray-50 rounded-sm p-4 border border-gray-200 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Cost Price</p>
                  <p className="font-heading font-bold text-lg text-[#1F2937]">{formatCurrency(product.cost_price)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Selling Price</p>
                  <p className="font-heading font-bold text-lg text-[#1F2937]">{formatCurrency(product.selling_price)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Profit</p>
                  <p className={`font-heading font-bold text-lg ${product.profit_amount >= 0 ? "text-[#10B981]" : "text-[#E63946]"}`}>{formatCurrency(product.profit_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Margin</p>
                  <p className={`font-heading font-bold text-lg flex items-center gap-1 ${product.profit_margin >= 0 ? "text-[#10B981]" : "text-[#E63946]"}`}>
                    <TrendingUp className="w-4 h-4" /> {product.profit_margin?.toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                ["Category", product.category_info?.name || "-"],
                ["Unit of Measure", product.unit_of_measure],
                ["Quantity in Stock", product.quantity_in_stock],
                ["Reorder Level", product.reorder_level],
                ["Dimensions", product.length ? `${product.length} x ${product.width} x ${product.height} ${product.dimension_unit}` : "-"],
                ["Weight", product.weight ? `${product.weight} ${product.weight_unit}` : "-"],
                ["Supplier", product.supplier_info?.company_name || "-"],
                ["Status", product.status],
                ["Date Added", formatDateLong(product.created_at)],
                ["Last Updated", formatDateLong(product.updated_at)],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
                  <p className="text-sm text-[#1F2937]">{value || "-"}</p>
                </div>
              ))}
            </div>

            {product.description && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Description</p>
                <p className="text-sm text-[#1F2937] whitespace-pre-wrap">{product.description}</p>
              </div>
            )}

            {product.supplier_info && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Supplier Information</p>
                <button onClick={() => navigate(`/suppliers/${product.supplier_info.supplier_id}`)} className="text-sm text-[#E63946] hover:underline">
                  {product.supplier_info.company_name} - {product.supplier_info.contact_person || "No contact"}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">Edit Product</DialogTitle></DialogHeader>
          <ProductForm form={form} setForm={setForm} categories={categories} suppliers={suppliers} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
